// Write ✓ → Who gets it → Check → Send. The server refuses to send while any check is red,
// so this page only has to make the checks easy to turn green.

import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { api, ApiError, errorText } from '../lib/api';
import { navigate } from '../lib/router';
import { Button, Notice, Spinner, confirmBox, toast, toastError } from '../lib/ui';
import { Icon } from '../lib/icons';
import { useSettings, useSettingsState, readySender, loadSettings } from '../lib/store';
import { previewHtml } from '../lib/preview';
import { centralToIso, centralYmd, clock, num, plural, when, TZ } from '../lib/format';
import { InboxPreview, MailMessage } from '../parts/InboxPreview';
import { findPlanned } from '../../shared/plan';
import type { Audience, CheckItem, EmailRow } from '../../shared/types';

type Step = 1 | 2 | 3;

interface Checks {
  items: CheckItem[];
  audienceCount: number;
  testSentAt: string | null;
  testEmails: string[];
}

interface TagCount {
  tag: string;
  active: number;
  total: number;
}

export function SendSteps({ id }: { id: number }) {
  const { bundle, error: settingsErr, retry: retrySettings } = useSettingsState();
  const [email, setEmail] = useState<EmailRow | null>(null);
  const [inSequence, setInSequence] = useState(false);
  const [count, setCount] = useState<number | null>(null);
  const [tags, setTags] = useState<TagCount[] | null>(null);
  const [byStatus, setByStatus] = useState<Record<string, number> | null>(null);
  const [step, setStep] = useState<Step>(1);
  useEffect(() => window.scrollTo({ top: 0, behavior: 'smooth' }), [step]);
  const [checks, setChecks] = useState<Checks | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = async () => {
    try {
      const r = await api<{ email: EmailRow; audienceCount: number; inSequence: boolean }>(`/api/emails/${id}`);
      if (!['draft', 'scheduled'].includes(r.email.status)) {
        navigate(`/emails/${id}`, { replace: true });
        return;
      }
      setEmail(r.email);
      setCount(r.audienceCount);
      setInSequence(r.inSequence);
      if (r.email.status === 'scheduled') setStep(3);
    } catch (e) {
      setErr(errorText(e));
    }
  };

  useEffect(() => {
    load();
    api<{ tags: TagCount[] }>('/api/tags').then((r) => setTags(r.tags), () => setTags([]));
    api<{ byStatus: Record<string, number> }>('/api/contacts?page=0').then((r) => setByStatus(r.byStatus), () => {});
  }, [id]);

  const loadChecks = async () => {
    try {
      setChecks(await api<Checks>(`/api/emails/${id}/checks`));
    } catch (e) {
      toastError(e);
    }
  };
  useEffect(() => {
    if (step === 2 || step === 3) loadChecks();
  }, [step]);

  // When the server says the list changed, refresh the count and the checks together.
  // Until the checks are back, the send button stays off so it can't send the old number.
  const refresh = async () => {
    setChecks(null);
    await Promise.all([load(), loadChecks()]);
  };

  const setAudience = async (a: Audience) => {
    if (!email) return;
    setEmail({ ...email, audience: a });
    setCount(null);
    try {
      await api(`/api/emails/${id}`, { method: 'PUT', body: { audience: a } });
      const c = await api<{ count: number }>(`/api/contacts/count?audience=${encodeURIComponent(JSON.stringify(a))}`);
      setCount(c.count);
      const fresh = await api<{ email: EmailRow }>(`/api/emails/${id}`);
      setEmail(fresh.email);
    } catch (e) {
      toastError(e);
    }
  };

  if (err)
    return (
      <div class="wrap">
        <Notice kind="bad">{err}</Notice>
      </div>
    );
  if (!bundle && settingsErr)
    return (
      <div class="wrap">
        <Notice kind="bad" action={<Button onClick={retrySettings}>Try again</Button>}>
          {settingsErr}
        </Notice>
      </div>
    );
  if (!email || !bundle) return <Spinner label="Getting things ready…" />;

  const blockers = checks ? checks.items.filter((c) => c.level === 'block' && !c.ok) : [];

  return (
    <div class="steps-page">
      <header class="ed-top steps-top">
        <button class="ed-back" onClick={() => navigate(`/emails/${id}/edit`)} aria-label="Back to writing">
          <Icon name="back" size={20} />
          <span class="hide-phone">Back to writing</span>
        </button>
        <div class="ed-name">
          <b>{email.name}</b>
        </div>
        <button class="text-link" onClick={() => navigate('/')}>
          Save and close
        </button>
      </header>

      <Stepper step={step} onGo={(s) => (s === 0 ? navigate(`/emails/${id}/edit`) : setStep(s as Step))} canGo={(s) => s <= step || (s === 3 && !!checks && blockers.length === 0)} />

      {inSequence ? (
        <div class="wrap narrow">
          <Notice kind="info" icon="repeat" action={<Button onClick={() => navigate('/automatic')}>Open Automatic</Button>}>
            This email is part of an automatic series. It goes out from there, not from here.
          </Notice>
        </div>
      ) : (
        <main class="steps-main">
          <div class="steps-left">
            {step === 1 && (
              <WhoStep
                email={email}
                count={count}
                tags={tags}
                byStatus={byStatus}
                onAudience={setAudience}
                onNext={() => setStep(2)}
              />
            )}
            {step === 2 && <CheckStep id={id} facts={(email.library_key && findPlanned(email.library_key)?.verify) || []} checks={checks} reload={loadChecks} onBack={() => setStep(1)} onNext={() => setStep(3)} blockers={blockers.length} />}
            {step === 3 && <SendStep email={email} count={checks?.audienceCount ?? count ?? 0} ready={!!checks} onBack={() => setStep(2)} reload={refresh} blockers={blockers} />}
          </div>
          <aside class="steps-side">
            <h2 class="side-title">In her inbox</h2>
            <InboxPreview compact fromName={bundle.settings.from_name || 'LaVir Med Spa'} subject={email.subject} preheader={email.preheader} />
            <h2 class="side-title side-title-2">When she opens it</h2>
            <PhonePreview email={email} fromName={bundle.settings.from_name || 'LaVir Med Spa'} />
          </aside>
        </main>
      )}
    </div>
  );
}

function Stepper({ step, onGo, canGo }: { step: Step; onGo: (s: number) => void; canGo: (s: number) => boolean }) {
  const names = ['Write', 'Who gets it', 'Check', 'Send'];
  return (
    <nav class="stepper" aria-label="Steps">
      {names.map((n, i) => {
        const state = i < step ? 'done' : i === step ? 'now' : '';
        return (
          <div class="stepper-item" key={n}>
            {i > 0 && <div class={`stepper-line ${i <= step ? 'done' : ''}`} />}
            <button class={`stepper-step ${state}`} onClick={() => canGo(i) && onGo(i)} disabled={!canGo(i) && i !== 0} aria-current={i === step ? 'step' : undefined}>
              <i>{i < step ? <Icon name="check" size={16} stroke={2.6} /> : i + 1}</i>
              <span>{n}</span>
            </button>
          </div>
        );
      })}
    </nav>
  );
}

// ---------- 1. who ----------

function WhoStep({
  email,
  count,
  tags,
  byStatus,
  onAudience,
  onNext,
}: {
  email: EmailRow;
  count: number | null;
  tags: TagCount[] | null;
  byStatus: Record<string, number> | null;
  onAudience: (a: Audience) => void;
  onNext: () => void;
}) {
  const a = email.audience;
  const skipped = byStatus ? byStatus.unsubscribed + byStatus.bounced + byStatus.complained : 0;
  const usable = (tags ?? []).filter((t) => t.active > 0);
  return (
    <>
      <h1 class="display">Who gets it?</h1>
      <div class="choice-list">
        <button class={`choice ${a.kind === 'all' ? 'on' : ''}`} onClick={() => onAudience({ kind: 'all' })}>
          <span class="radio" />
          <span>
            <b>Everyone</b>
          </span>
        </button>
        <div class={`choice ${a.kind === 'tag' ? 'on' : ''}`}>
          <button class="choice-head" onClick={() => usable[0] && onAudience({ kind: 'tag', tag: a.kind === 'tag' ? a.tag : usable[0].tag })} disabled={!usable.length}>
            <span class="radio" />
            <span>
              <b>Only some people</b>
              {!usable.length && <small>Add a tag when you import contacts.</small>}
            </span>
          </button>
          {a.kind === 'tag' && (
            <div class="tag-picks">
              {usable.map((t) => (
                <button key={t.tag} class={`tag-pick ${a.tag === t.tag ? 'on' : ''}`} onClick={() => onAudience({ kind: 'tag', tag: t.tag })}>
                  {t.tag} <small>{num(t.active)}</small>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <div class="count-line">
        {count == null ? (
          <span class="spin" />
        ) : (
          <span>
            <span>
              <b>{plural(count, 'client')}</b> will get this.
            </span>
            {skipped > 0 && <small class="muted count-skip">{num(skipped)} asked not to get emails or have a bad address.</small>}
          </span>
        )}
      </div>
      <div class="step-nav">
        <Button kind="primary" size="l" icon="next" onClick={onNext} disabled={!count}>
          Next: check it
        </Button>
      </div>
    </>
  );
}

// ---------- 2. check ----------

const FIX_IN_EDITOR = new Set(['subject', 'subject-length', 'preheader', 'links', 'images', 'alt', 'filler', 'blanks', 'size']);

// Rows that always show: the test (she acts on it every time) and who gets it (she may want to change it).
// Every row that is not ok always shows too, so a failing safety check can never be folded away.
const ALWAYS_SHOW = new Set(['test', 'audience']);

function CheckStep({ id, facts, checks, reload, onBack, onNext, blockers }: { id: number; facts: string[]; checks: Checks | null; reload: () => Promise<void>; onBack: () => void; onNext: () => void; blockers: number }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  // Rows that just turned green get a small pop. Only a change counts, not the first load.
  const lastOk = useRef<Record<string, boolean> | null>(null);
  const [justOk, setJustOk] = useState<string[]>([]);
  useEffect(() => {
    if (!checks) return;
    const prev = lastOk.current;
    if (prev) setJustOk(checks.items.filter((c) => c.ok && prev[c.id] === false).map((c) => c.id));
    lastOk.current = Object.fromEntries(checks.items.map((c) => [c.id, c.ok]));
  }, [checks]);
  if (!checks) return <Spinner label="Checking your email…" />;

  const test = async () => {
    setBusy('test');
    try {
      await api(`/api/emails/${id}/test`, { method: 'POST' });
      toast(`Test sent to ${checks.testEmails[0] || 'you'}. Open it on your phone.`);
      await reload();
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(null);
    }
  };
  const approve = async () => {
    setBusy('approve');
    try {
      await api(`/api/emails/${id}/approve`, { method: 'POST' });
      await reload();
    } catch (e) {
      toastError(e);
      await reload();
    } finally {
      setBusy(null);
    }
  };

  const testItem = checks.items.find((c) => c.id === 'test');
  // A test is out and only needs her OK (not a new test after an edit).
  const testWaiting = !!checks.testSentAt && !!testItem && !/changed/.test(testItem.detail);
  const action = (c: CheckItem) => {
    if (c.id === 'test' && !c.ok)
      return testWaiting ? (
        <span class="check-actions">
          <Button size="s" kind="quiet" busy={busy === 'test'} onClick={test}>
            Send again
          </Button>
          <Button size="s" kind="primary" icon="check" busy={busy === 'approve'} onClick={approve}>
            It looks right
          </Button>
        </span>
      ) : (
        <Button size="s" kind="primary" icon="send" busy={busy === 'test'} onClick={test}>
          Send me a test
        </Button>
      );
    if (FIX_IN_EDITOR.has(c.id) && !c.ok)
      return (
        <button class="text-link" onClick={() => navigate(`/emails/${id}/edit`)}>
          Fix
        </button>
      );
    if (c.id === 'subject' && c.ok)
      return (
        <button class="text-link" onClick={() => navigate(`/emails/${id}/edit`)}>
          Edit
        </button>
      );
    if (c.id === 'audience')
      return (
        <button class="text-link" onClick={onBack}>
          Change
        </button>
      );
    if ((c.id === 'sender' || c.id === 'footer') && !c.ok)
      return (
        <button class="text-link" onClick={() => navigate('/settings')}>
          Settings
        </button>
      );
    return null;
  };

  const shown = (c: CheckItem) => !c.ok || ALWAYS_SHOW.has(c.id) || justOk.includes(c.id);
  const passed = checks.items.filter((c) => !shown(c));
  const rows = showAll ? checks.items : checks.items.filter(shown);

  return (
    <>
      <h1 class="display">Check before you send</h1>
      {!blockers && <p class="lead check-lead">All set. Ready when you are.</p>}
      <div class="check-list check-card">
        {rows.map((c) => {
          const state = c.ok ? 'ok' : c.level === 'block' ? (c.id === 'test' ? 'wait' : 'bad') : 'warn';
          const pop = justOk.indexOf(c.id);
          return (
            <div key={c.id} class={`check check-${state}${pop >= 0 ? ' check-pop' : ''}`} style={pop >= 0 ? `--d:${pop}` : undefined}>
              <span class="check-ic">
                <Icon name={state === 'ok' ? 'check' : state === 'wait' ? 'clock' : 'alert'} size={15} stroke={2.4} />
              </span>
              <div>
                <b>{c.title}</b>
                {c.detail && <span>{c.detail}</span>}
                {c.id === 'test' && checks.testSentAt && !c.ok && <span class="muted"> Sent {when(checks.testSentAt)}.</span>}
              </div>
              {action(c)}
            </div>
          );
        })}
        {passed.length > 0 &&
          (showAll ? (
            <button class="check check-fold check-fold-open" onClick={() => setShowAll(false)} aria-expanded="true">
              <span class="text-link">Hide passed checks</span>
            </button>
          ) : (
            <button class="check check-ok check-fold" onClick={() => setShowAll(true)} aria-expanded="false">
              <span class="check-ic">
                <Icon name="check" size={15} stroke={2.4} />
              </span>
              <div>
                <b>{`${passed.length} other check${passed.length > 1 ? 's' : ''} passed`}</b>
              </div>
              <span class="text-link">Show</span>
            </button>
          ))}
      </div>
      {facts.length > 0 && (
        <div class="sheet-verify check-facts">
          <b>
            <Icon name="alert" size={16} /> Before you send, check these
          </b>
          <ul>
            {facts.map((f) => (
              <li key={f}>{f}</li>
            ))}
          </ul>
          <button class="text-link" onClick={() => navigate(`/emails/${id}/edit`)}>
            Change it in the email
          </button>
        </div>
      )}
      <div class="step-nav">
        <Button kind="quiet" icon="back" onClick={onBack}>
          Back
        </Button>
        <Button kind="primary" size="l" icon="next" onClick={onNext} disabled={blockers > 0}>
          {blockers ? (testItem && !testItem.ok && blockers === 1 ? (testWaiting ? 'Tap "It looks right" first' : 'Send a test first') : `${blockers} thing${blockers > 1 ? 's' : ''} to fix`) : 'Next: pick a time'}
        </Button>
      </div>
    </>
  );
}

// ---------- 3. send ----------

function SendStep({ email, count, ready, onBack, reload, blockers }: { email: EmailRow; count: number; ready: boolean; onBack: () => void; reload: () => Promise<void>; blockers: CheckItem[] }) {
  const bundle = useSettings();
  const s = bundle!.settings;
  const sender = readySender(bundle);
  const conf = s.providers.find((p) => p.name === sender?.name);
  const [mode, setMode] = useState<'now' | 'later'>('now');
  const tomorrow = centralYmd(new Date(Date.now() + 86_400_000));
  const [date, setDate] = useState(tomorrow);
  const [time, setTime] = useState('10:00');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    loadSettings(true).catch(() => {});
  }, []);

  const estimate = useMemo(() => {
    if (!conf) return '';
    const perMin = conf.perMinute;
    const mins = Math.ceil(count / perMin);
    const hours = Math.round((mins / 60) * 10) / 10;
    const dur = mins < 90 ? `about ${mins} minutes` : `about ${hours} hours`;
    // Past the daily limit the day count is the real answer; the hours figure would contradict it.
    if (count > conf.dailyLimit) {
      return `Goes out ${perMin} a minute, up to ${num(conf.dailyLimit)} a day, so Gmail stays happy. All ${num(count)} get it within ${Math.ceil(count / conf.dailyLimit)} days.`;
    }
    return `Goes out ${perMin} a minute so Gmail stays happy. That takes ${dur}.`;
  }, [conf, count]);

  const hourNow = Number(new Date().toLocaleString('en-US', { timeZone: TZ, hour: 'numeric', hour12: false }));
  const outsideHours = s.send_start_hour !== s.send_end_hour && (hourNow < s.send_start_hour || hourNow >= s.send_end_hour);
  const hourLabel = (h: number) => clock(new Date(Date.UTC(2026, 0, 1, h + 6)), TZ); // Jan: Chicago is UTC-6

  if (email.status === 'scheduled')
    return (
      <>
        <h1 class="display">It's scheduled</h1>
        <div class="sched-card">
          <Icon name="calendar" size={22} />
          <div>
            <b>Goes out {when(email.scheduled_at)}</b>
            <span>To {plural(count, 'person', 'people')}. You can change your mind until then.</span>
          </div>
        </div>
        <div class="step-nav">
          <Button
            kind="quiet"
            busy={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await api(`/api/emails/${email.id}/unschedule`, { method: 'POST' });
                toast("Taken off the schedule. It won't go out.");
                await reload();
              } catch (e) {
                toastError(e);
              } finally {
                setBusy(false);
              }
            }}
          >
            Don't send it
          </Button>
          <Button kind="primary" onClick={() => navigate('/')}>
            Done
          </Button>
        </div>
      </>
    );

  const go = async () => {
    const whenIso = mode === 'later' ? centralToIso(date, time) : null;
    if (mode === 'later' && !whenIso) {
      toast(date && time ? 'That time is skipped when the clocks change. Pick another time.' : 'Pick a day and a time.', 'bad');
      return;
    }
    const ok = await confirmBox({
      title: mode === 'now' ? `Send to ${plural(count, 'person', 'people')}?` : `Schedule for ${when(whenIso)}?`,
      body: (
        <>
          <p>
            <b>{email.subject}</b>
          </p>
          <p class="muted">{mode === 'now' ? 'It starts in about a minute. You can pause it any time from the report.' : 'You can cancel it until it starts.'}</p>
        </>
      ),
      ok: mode === 'now' ? 'Send it' : 'Schedule it',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api(`/api/emails/${email.id}/send`, { body: { confirmCount: count, ...(whenIso ? { when: whenIso } : {}) } });
      if (mode === 'now') navigate(`/emails/${email.id}?sent=1`, { replace: true });
      else {
        toast(`Scheduled for ${when(whenIso)}.`);
        navigate('/', { replace: true });
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && typeof e.data.audienceCount === 'number') {
        toast(e.message, 'info');
        await reload();
      } else toastError(e);
      setBusy(false);
    }
  };

  return (
    <>
      <h1 class="display">When should it go out?</h1>
      {blockers.length > 0 && <Notice kind="warn">Go back a step. {blockers[0].title} needs a fix first.</Notice>}
      <div class="choice-list">
        <button class={`choice ${mode === 'now' ? 'on' : ''}`} onClick={() => setMode('now')}>
          <span class="radio" />
          <span>
            <b>Now</b>
            <small>{outsideHours ? `Sending runs ${hourLabel(s.send_start_hour)} to ${hourLabel(s.send_end_hour)}, so it starts at ${hourLabel(s.send_start_hour)}.` : 'Starts in about a minute.'}</small>
          </span>
        </button>
        <div class={`choice ${mode === 'later' ? 'on' : ''}`}>
          <button class="choice-head" onClick={() => setMode('later')}>
            <span class="radio" />
            <span>
              <b>Later</b>
              <small>Pick a day and time (Central time).</small>
            </span>
          </button>
          {mode === 'later' && (
            <div class="when-pick">
              <input type="date" value={date} min={centralYmd()} max={centralYmd(new Date(Date.now() + 89 * 86_400_000))} onInput={(e) => setDate(e.currentTarget.value)} aria-label="Day" />
              <input type="time" value={time} step={900} onInput={(e) => setTime(e.currentTarget.value)} aria-label="Time" />
            </div>
          )}
        </div>
      </div>
      {estimate && (
        <p class="muted send-note">
          <Icon name="clock" size={15} /> {estimate}
        </p>
      )}
      <div class="step-nav">
        <Button kind="quiet" icon="back" onClick={onBack}>
          Back
        </Button>
        <Button kind="primary" size="l" icon={mode === 'now' ? 'send' : 'calendar'} busy={busy} onClick={go} disabled={blockers.length > 0 || !count || !ready}>
          {mode === 'now' ? `Send to ${plural(count, 'person', 'people')}` : 'Schedule it'}
        </Button>
      </div>
    </>
  );
}

function PhonePreview({ email, fromName }: { email: EmailRow; fromName: string }) {
  const bundle = useSettings();
  const html = useMemo(() => previewHtml(email, bundle?.settings), [email, bundle]);
  return (
    <MailMessage fromName={fromName} subject={email.subject}>
      <iframe srcdoc={html} title="Phone preview" />
    </MailMessage>
  );
}
